/*
 *   Copyright 2022-2024 SenX S.A.S.
 *
 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { h } from "@stencil/core";
import { DataModel } from "../../model/types";
import { Param } from "../../model/param";
import { Logger } from "../../utils/logger";
import { GTSLib } from "../../utils/gts.lib";
import { Utils } from "../../utils/utils";
import html2canvas from "html2canvas";
export class DiscoveryTabular {
    constructor() {
        this.divider = 1000;
        this.result = undefined;
        this.type = undefined;
        this.options = Object.assign(Object.assign({}, new Param()), { timeMode: 'date' });
        this.width = undefined;
        this.height = undefined;
        this.debug = false;
        this.unit = undefined;
        this.parsing = false;
        this.rendering = false;
        this.tabularData = [];
    }
    updateRes() {
        this.tabularData = this.convert(GTSLib.getData(this.result));
    }
    async resize() {
        const dims = Utils.getContentBounds(this.el.parentElement);
        this.width = dims.w;
        this.height = dims.h;
        return Promise.resolve();
    }
    // noinspection JSUnusedLocalSymbols
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    async export(type = 'png') {
        return (await html2canvas(this.pngWrapper, {
            allowTaint: true,
            backgroundColor: this.options.bgColor || Utils.getCSSColor(this.el, '--warp-view-tile-background', '#fff'),
        })).toDataURL();
    }
    componentWillLoad() {
        var _a;
        this.parsing = true;
        this.LOG = new Logger(DiscoveryTabular, this.debug);
        if (typeof this.options === 'string') {
            this.options = JSON.parse(this.options);
        }
        this.result = GTSLib.getData(this.result);
        this.divider = GTSLib.getDivider(this.options.timeUnit || 'us');
        this.tabularData = this.convert(this.result || new DataModel());
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['componentWillLoad'], {
            type: this.type,
            options: this.options,
        });
        const dims = Utils.getContentBounds(this.el.parentElement);
        this.width = dims.w;
        this.height = dims.h;
        this.el.addEventListener('mouseout', () => this.dataPointOver.emit({}));
    }
    static getHeaderParam(data, i, j, key, def) {
        return data.params && data.params[i] && data.params[i][key] && data.params[i][key][j]
            ? data.params[i][key][j]
            : data.globalParams && data.globalParams[key] && data.globalParams[key][j]
                ? data.globalParams[key][j]
                : def;
    }
    handleDataPointOver(event) {
        event.stopImmediatePropagation();
        this.dataPointOver.emit(event.detail);
    }
    handleDataPointSelected(event) {
        event.stopImmediatePropagation();
        this.dataPointSelected.emit(event.detail);
    }
    convert(data) {
        var _a;
        let options = Utils.mergeDeep(Object.assign(Object.assign({}, new Param()), { timeMode: 'date' }), this.options || {});
        options = Utils.mergeDeep(options || {}, data.globalParams);
        this.options = Object.assign({}, options);
        this.params = data.params || [];
        let dataGrid;
        if (GTSLib.isArray(data.data)) {
            const dataList = GTSLib.flatDeep(data.data);
            (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['convert', 'isArray'], dataList, options);
            if (data.data.length > 0 && GTSLib.isGts(dataList[0])) {
                dataGrid = this.parseData(data, dataList);
            }
            else {
                dataGrid = this.parseCustomData(data, dataList);
            }
        }
        else {
            dataGrid = this.parseCustomData(data, [data.data]);
        }
        this.parsing = false;
        return dataGrid;
    }
    parseCustomData(dataModel, data) {
        var _a;
        const flatData = [];
        data.forEach(d => {
            if (d !== null && d !== undefined) {
                const dataSet = {
                    name: (d === null || d === void 0 ? void 0 : d.title) || '',
                    values: (d === null || d === void 0 ? void 0 : d.rows) || [],
                    headers: (d === null || d === void 0 ? void 0 : d.columns) || [],
                    isGTS: false,
                    params: (d === null || d === void 0 ? void 0 : d.params) || [],
                };
                flatData.push(dataSet);
            }
        });
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['parseCustomData', 'flatData'], flatData, dataModel);
        return flatData;
    }
    parseData(dataModel, data) {
        var _a, _b;
        const flatData = [];
        (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['parseData'], data);
        data.forEach((d, i) => {
            var _a, _b;
            const dataSet = {
                name: '',
                values: [],
                headers: [],
                isGTS: false,
                params: this.params
            };
            if (GTSLib.isGts(d)) {
                (_a = this.LOG) === null || _a === void 0 ? void 0 : _a.debug(['parseData', 'isGts'], d);
                dataSet.name = ((dataModel.params || [])[i] || { key: undefined }).key || GTSLib.serializeGtsMetadata(d);
                dataSet.values = d.v; // .map(v => [this.formatDate(v[0])].concat(v.slice(1, v.length)));
                dataSet.isGTS = true;
            }
            else {
                (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.debug(['parseData', 'is not a Gts'], d);
                dataSet.values = GTSLib.isArray(d) ? d : [d];
            }
            dataSet.headers = [DiscoveryTabular.getHeaderParam(dataModel, i, 0, 'headers', 'Date')];
            if (d.v && d.v.length > 0 && d.v[0].length > 2) {
                dataSet.headers.push(DiscoveryTabular.getHeaderParam(dataModel, i, 1, 'headers', 'Latitude'));
            }
            if (d.v && d.v.length > 0 && d.v[0].length > 3) {
                dataSet.headers.push(DiscoveryTabular.getHeaderParam(dataModel, i, 2, 'headers', 'Longitude'));
            }
            if (d.v && d.v.length > 0 && d.v[0].length > 4) {
                dataSet.headers.push(DiscoveryTabular.getHeaderParam(dataModel, i, 3, 'headers', 'Elevation'));
            }
            if (d.v && d.v.length > 0) {
                dataSet.headers.push(DiscoveryTabular.getHeaderParam(dataModel, i, d.v[0].length - 1, 'headers', 'Value'));
            }
            if (dataSet.values.length > 0) {
                flatData.push(dataSet);
            }
        });
        (_b = this.LOG) === null || _b === void 0 ? void 0 : _b.debug(['parseData', 'flatData'], flatData, this.options);
        return flatData;
    }
    render() {
        this.draw.emit();
        return h("div", { key: '7297bf88186f787500ea89da2274975a51445f05', class: "tabular-wrapper", ref: (el) => this.pngWrapper = el }, h("div", { key: 'ca7c692b57badc91df79e241c76c287e10fe672e', class: "tabular-wrapper-inner" }, this.parsing ? h("discovery-spinner", null, "Parsing data...") : '', this.rendering ? h("discovery-spinner", null, "Rendering data...") : '', this.tabularData.map(d => h("discovery-pageable", { data: d, onDataPointOver: event => this.handleDataPointOver(event), onDataPointSelected: event => this.handleDataPointSelected(event), divider: this.divider, options: this.options, debug: this.debug }))));
    }
    static get is() { return "discovery-tabular"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["discovery-tabular.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["discovery-tabular.css"]
        };
    }
    static get properties() {
        return {
            "result": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "DataModel | string",
                    "resolved": "DataModel | string",
                    "references": {
                        "DataModel": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DataModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "result",
                "reflect": false
            },
            "type": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ChartType",
                    "resolved": "\"line\" | \"area\" | \"scatter\" | \"step-area\" | \"spline-area\" | \"spline\" | \"step\" | \"step-after\" | \"step-before\" | \"annotation\" | \"bar\" | \"bar-polar\" | \"display\" | \"image\" | \"map\" | \"gauge\" | \"linear-gauge\" | \"circle\" | \"compass\" | \"pie\" | \"doughnut\" | \"rose\" | \"tabular\" | \"svg\" | \"input:text\" | \"input:list\" | \"input:secret\" | \"input:autocomplete\" | \"input:chips\" | \"input:chips-autocomplete\" | \"input:slider\" | \"input:date\" | \"input:date-range\" | \"input:multi\" | \"input:multi-cb\" | \"button\" | \"button:radio\" | \"hidden\" | \"calendar\" | \"heatmap\" | \"profile\" | \"boxplot\" | \"dashboard\" | \"dashboard:flex\" | \"dashboard:scada\"",
                    "references": {
                        "ChartType": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::ChartType"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "type",
                "reflect": false
            },
            "options": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "Param | string",
                    "resolved": "Param | string",
                    "references": {
                        "Param": {
                            "location": "import",
                            "path": "../../model/param",
                            "id": "src/model/param.ts::Param"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "options",
                "reflect": false,
                "defaultValue": "{...new Param(), timeMode: 'date'}"
            },
            "width": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "width",
                "reflect": false
            },
            "height": {
                "type": "number",
                "mutable": true,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "height",
                "reflect": false
            },
            "debug": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "debug",
                "reflect": false,
                "defaultValue": "false"
            },
            "unit": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "attribute": "unit",
                "reflect": false
            }
        };
    }
    static get states() {
        return {
            "parsing": {},
            "rendering": {},
            "tabularData": {}
        };
    }
    static get events() {
        return [{
                "method": "draw",
                "name": "draw",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "void",
                    "resolved": "void",
                    "references": {}
                }
            }, {
                "method": "dataPointOver",
                "name": "dataPointOver",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "dataPointSelected",
                "name": "dataPointSelected",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "discoveryEvent",
                "name": "discoveryEvent",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "DiscoveryEvent",
                    "resolved": "DiscoveryEvent",
                    "references": {
                        "DiscoveryEvent": {
                            "location": "import",
                            "path": "../../model/types",
                            "id": "src/model/types.ts::DiscoveryEvent"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "resize": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            },
            "export": {
                "complexType": {
                    "signature": "(type?: 'png' | 'svg') => Promise<string>",
                    "parameters": [{
                            "name": "type",
                            "type": "\"svg\" | \"png\"",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "Param": {
                            "location": "import",
                            "path": "../../model/param",
                            "id": "src/model/param.ts::Param"
                        }
                    },
                    "return": "Promise<string>"
                },
                "docs": {
                    "text": "",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "result",
                "methodName": "updateRes"
            }];
    }
}
//# sourceMappingURL=discovery-tabular.js.map
